/* =============================================================================
   Theme model
   -----------------------------------------------------------------------------
   Three user choices: light, dark, or follow the system. The choice persists
   and is applied by an inline script in index.html before first paint, so a
   dark-mode user never sees a flash of the light theme.
   ============================================================================= */

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'dhdtech:theme';

/** The attribute tokens.css keys off. `system` removes it entirely. */
export const THEME_ATTRIBUTE = 'data-theme';

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function readStoredTheme(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeChoice(stored) ? stored : 'system';
  } catch {
    // Storage can throw in a private window or when site data is blocked.
    return 'system';
  }
}

export function storeTheme(choice: ThemeChoice): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // A failed write must never break the switch — the theme still applies.
  }
}

export function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? systemTheme() : choice;
}

/** Applies a resolved theme to the document root. */
export function applyTheme(choice: ThemeChoice): ResolvedTheme {
  const resolved = resolveTheme(choice);
  const root = document.documentElement;

  if (choice === 'system') {
    root.removeAttribute(THEME_ATTRIBUTE);
  } else {
    root.setAttribute(THEME_ATTRIBUTE, choice);
  }

  // Keeps native controls, scrollbars and form autofill in the right theme.
  root.style.colorScheme = resolved;
  return resolved;
}

/**
 * The script that runs before paint. Kept as a string so index.html and the
 * provider can never drift apart — both call this same source.
 */
export const NO_FLASH_SCRIPT = `(function(){try{var k='${THEME_STORAGE_KEY}';var v=localStorage.getItem(k);if(v==='light'||v==='dark'){document.documentElement.setAttribute('${THEME_ATTRIBUTE}',v);document.documentElement.style.colorScheme=v;}else{var d=window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches;document.documentElement.style.colorScheme=d?'dark':'light';}}catch(e){}})();`;
